<template>
  <div
    v-if="mediaQueryList && !$auth.loading"
    :class="['App column', {
      'mb-47': nonProdEnvironment,
    }]"
    id="app"
  >
    <!-- static positioned at the top (Desktop ONLY) -->
    <base-alerts
      v-if="announcements.length"
      :alerts="announcements"
      class="App__announcement"
      :center-message="true"
      :closeable="false"
      :fixed="true"
      :hide-icon="true"
    />

    <!-- fixed positioned at the top (Desktop ONLY) -->
    <base-alerts
      v-if="bankingAlerts.length"
      :alerts="bankingAlerts"
      :closeable="true"
    />

    <!-- fixed position at the bottom -->
    <base-alerts
      v-if="nonProdEnvironment"
      :alerts="[ { message: `${environment.toUpperCase()} - ${releaseVersion.toUpperCase()}` } ]"
      :class="['App__environment-label', {
        'bg-green': environment === 'staging',
        'bg-red': !['staging', 'qa'].includes(environment),
        'bg-yellow': environment === 'qa'
      }]"
      :center-message="true"
      :closeable="false"
      :hide-icon="true"
      :fixed="true"
    />

    <!-- fixed positioned at the top -->
    <vue-progress-bar />
    <!-- fixed positioned at the top -->
    <toastr-alert />
    <!-- Exit button for controlling client -->
    <base-button
      v-if="controlledClient"
      @click="exitControl"
      class="bg-red fc-white"
      data-cy="exit-control-btn"
    >
      Exit Control
    </base-button>
    <!-- BANKING MODAL (CLIENT AND DESKTOP ONLY) -->
    <transition name="Transition__opacity-fade">
      <base-modal
        v-if="showBankRequiredModal"
        data-cy="bank-required-modal"
      >
        <template #label>
          Welcome to the Bobtail Factoring App! If you have already been onboarded, please set up your bank account information.<br><br>If you are new here, please contact our support team for onboarding at <a
            class="fc-blue fs-20 fw-med mb-24"
            href="tel:+1(410)204-2084">+1 (410) 204 2084</a>.
        </template>
        <template #actions>
          <base-button
            @click="goToClientBanking"
            class="bg-blue fc-white"
            data-cy="bank-required-modal-go-to-bank-btn"
          >
            Go to banking
          </base-button>
        </template>
      </base-modal>
    </transition>
    <!-- UPDATE AVAILABLE MODAL (version check) -->
    <transition name="Transition__opacity-fade">
      <base-modal
        v-if="hashChanged"
        data-cy="refresh-required-modal"
      >
        <template #label>
          An update is available. Please click update below to use the new version.
        </template>
        <template #actions>
          <base-button
            @click="reloadApp"
            class="bg-blue fc-white"
            data-cy="refresh-modal-update-btn"
          >
            Update
          </base-button>
        </template>
      </base-modal>
    </transition>
    <!-- RATE INCREASE PROMPT (clients only, must match an ID) -->
    <transition name="Transition__opacity-fade">
      <base-modal
        v-if="showRateIncreasePrompt"
        data-cy="rate-increase-modal"
      >
        <template #label>
          Due to rising interest rates, all invoices will now be approved at a rate of {{ new Big($auth.user.client.factor_rate_percentage) }}%. If you have any questions, please contact your support team.
        </template>
        <template #actions>
          <base-button
            @click="acknowledgeRateIncrease"
            class="bg-blue fc-white"
            data-cy="rate-increase-modal-acknowledge-btn"
          >
            Ok
          </base-button>
        </template>
      </base-modal>
    </transition>

    <div
      v-if="ready"
      :class="['App__wrapper', {
        'row': !mobile.isMobile,
        'column': mobile.isMobile,
      }]">
      <navigation />

      <div class="column">
        <!-- These two components should only be visible on certain pages, if a user exists,
        and if it's desktop -->
        <div
          v-if="$auth.user && !mobile.isMobile && visible"
          class="row row--justify-between row--align-end"
        >
          <base-select-search />
          <!-- "Welcome, [client]" for clients on desktop -->
          <div
            v-if="$auth.user.client"
            class="App__client-welcome-text-container row row--align-center row--width-auto"
          >
            <label class="fc-light mr-4">Welcome,</label>
            <label
              class="fw-med"
              data-cy="client-shortened-name"
            >
              {{ $auth.user.client.shortened_name }}
            </label>
          </div>
        </div>
        <router-view :class="['App__router-view', { 'mobile-browsing': mobile.isMobile }]" />
      </div>
    </div>
    <transition name="Transition__opacity-fade">
      <base-modal v-if="showNewVersionModal">
        <template #label>
          You are enabled for Staff App V2.
        </template>
        <template #actions>
          <div class="App__new-version">
            <base-button
              @click="closeNewVersionModal"
              class="bg-light fc-white"
            >
              Stay Here
            </base-button>
            <base-button
              @click="redirectToV2"
              class="bg-blue fc-white"
            >
              Use New Version
            </base-button>
          </div>
        </template>
      </base-modal>
    </transition>
  </div>
</template>

<script>
import 'floating-vue/dist/style.css'
import 'vue-select/dist/vue-select.css'
import Big from 'big.js'
// Helpers
import bobtailAPI from './utils/axiosConfig'
import {
  Announcement,
  Client,
  ClientBankAccount,
} from './utils/api'
import FactoringAPI from './utils/FactoringAPI'
import {
  LDFlags,
} from './utils/constants'
import {
  LaunchDarkly,
  launchDarklyObservables,
} from './utils/launchDarkly'
// Components
import BaseAlerts from './components/base-alerts.vue'
import BaseButton from './components/base-button.vue'
import BaseModal from './components/base-modal.vue'
import BaseSelectSearch from './components/base-select-search.vue'
import Navigation from './components/navigation.vue'
// Mixins
import { refreshPageMixin } from './utils/refresh-page.mixin'

export default {
  name: 'App',

  components: {
    BaseAlerts,
    BaseButton,
    BaseModal,
    BaseSelectSearch,
    Navigation,
  },

  mixins: [ refreshPageMixin ],

  async created () {
    this.$store.commit('STORE_DEBTOR_NOT_FOUND_ID', await new FactoringAPI().getUnknownDebtorID())
  },

  mounted () {
    this.initVersionCheck('/version.html')

    window.addEventListener('beforeinstallprompt', (e) => {
      this.deferredPrompt = e

      this.deferredPrompt.prompt()

      this.deferredPrompt.userChoice.then((choice) => {
        if (choice === 'accepted') {
          alert('ACCEPTED')
        }
        else {
          alert('REJECTED')
        }
        this.deferredPrompt = null
      })
    })
  },

  data () {
    return {
      activeBank: null,
      alertManuallyClosed: false,
      announcements: [],
      auth: this.$auth,
      bankingAlerts: [],
      clientBanks: [],
      controlledClient: null,
      deferredPrompt: null,
      environment: process.env.VUE_APP_ENV,
      mediaQueryList: null,
      nonProdEnvironment: ![ 'production', 'demo' ].includes(process.env.VUE_APP_ENV),
      ready: false,
      releaseVersion: process.env.VUE_APP_GIT_RELEASE_VERSION,
      timer: '',
      visible: false,
      showNewVersionModal: false,
    }
  },

  async beforeDestroy () {
    this.cancelAutoUpdate()
  },

  computed: {
    clientWithoutBankModal () {
      return this.$store.getters.clientWithoutBankModal
    },

    mobile () {
      return this.$store.getters.mobile
    },

    launchDarklyObservables () {
      return launchDarklyObservables
    },

    showBankRequiredModal () {
      if (
        // Client doesn't have a bank
        this.clientWithoutBankModal
        // We're not on mobile
        && !this.mediaQueryList.matches
        // The route isn't one of these
        && ![ 'dashboard-client-banking', 'login', 'maintenance' ].includes(this.$route.name)
      ) {
        return true
      }
      return false
    },

    showRateIncreasePrompt () {
      return this.launchDarklyObservables['show-rate-increase-pop-up'] && !this.$auth.user.client?.metadata?.factoringRateIncrease?.accepted
    },
  },

  watch: {
    async '$auth.loading' () {
      if (!this.$auth.loading) {
        // Non-auth required initialization
        this.appUnauthenticatedInitialization()

        // Auth required initialization
        if (this.$auth.isAuthenticated()) {
          // Since /me is blocked during maintenance mode, we
          // cannot load the application with anything requiring auth.
          // However, master employees are allowed to authenticate to
          // manipulate maintenance mode and announcements.
          const maintenanceOn = (await new FactoringAPI().getMaintenanceMode()).isEnabled
          if (!maintenanceOn || this.$auth.user.employee.is_master) {
            await this.appAuthenticatedInitialization()
          }

        }

        // Prevents navigation and router-view from loading before announcements, which causes a UI
        // jump
        this.ready = true
      }
    },

    // User data (user, activeBank, clientBanks, controlledClient, etc) need reset when logging out
    $route (newRoute) {
      if ([ 'admin-login', 'login' ].includes(newRoute.name)) {
        this.resetData()
        this.createdFinished = true
      }
      // When watching the route, newRoute is far more accurate for the "current" route, or
      // at least the route we want when checking for navigation's visibility
      // By checking the route we're GOING to, it's a faster, more predictable method
      // of controlling when the navigation is displayed
      this.setSelectVisibility(newRoute)
    },
  },

  methods: {
    async acknowledgeRateIncrease () {
      try {
        // Protection against users running multiple tabs or windows of the app
        const updatedUser = (await (await bobtailAPI()).get('/api/users/me')).data
        if (!updatedUser.client?.metadata?.factoringRateIncrease?.accepted) {
          await Client.update({
            id: this.$auth.user.client.id,
            metadata: {
              ...this.$auth.user.client.metadata,
              factoringRateIncrease: {
                accepted: true,
                timestamp: new Date(),
              },
            },
          })
          await this.$auth.refreshAuthUserInfo()
        }
      }
      catch (error) {
        this.CError(error)
        this.captureSentryEvent(
          'appAuthenticatedInitialization "Acknowledge Rate Increase"',
          {
            config: error.config,
            data: this.$data,
            details: error,
            props: this.$props,
            response: error.response,
          }
        )
        this.requestFailure({ message: 'There was an issue updating the client\'s metadata' })
      }
    },

    addMediaQueryListener () {
      this.mediaQueryList.addListener(this.updateMatch)
    },

    async appAuthenticatedInitialization () {
      // Look for controlled client
      this.controlledClient = JSON.parse(localStorage.getItem('controlledClient'))

      await this.getNotifications()
    },

    appUnauthenticatedInitialization () {
      // Set matchMedia to update mobile state app-wide
      this.initMediaQueryList()

      this.setSelectVisibility()
    },

    Big,

    cancelAutoUpdate () {
      clearInterval(this.timer)
    },

    async checkForAnnouncements () {
      const alerts = []
      const announcements = (await Announcement.queryList({
        sort_by: 'created_at',
        sort_direction: 'ASC',
      })).data.rows.filter((announcement) => {
        return announcement.enabled
      })

      for (const announcement of announcements) {
        alerts.push(this.createAlert(announcement, true))
      }

      return alerts
    },

    async checkForBankAlerts () {
      const alerts = []
      if (this.$auth.user?.client) {
        // Get user's bank account info
        await this.getClientBanks(this.$auth.user.client.id)

        if (!this.clientBanks.length || !this.clientBanks.some((bank) => {
          return bank.status === 'active'
        })) {
          this.$store.commit('STORE_CLIENT_WITHOUT_BANK_MODAL', true)
        }
        else {
          // This, and the next else statement, are to prevent changing users and the bank modal
          // staying active whether relevant or not
          this.$store.commit('STORE_CLIENT_WITHOUT_BANK_MODAL', false)
          this.activeBank = this.clientBanks.filter((bank) => {
            return bank.status === 'active'
          })[0]
        }
      }
      else {
        // Disable for all employees
        this.$store.commit('STORE_CLIENT_WITHOUT_BANK_MODAL', false)
      }

      // Only clients flagged in LD should be displayed specific status bank notifications
      if (!await this.shouldPromptClientAboutBankVerification()) {
        return alerts
      }

      if (this.activeBank) {
        if ([ 'ITEM_LOGIN_REQUIRED', 'VERIFICATION_EXPIRED' ].includes(this.activeBank.plaid_verification_status)) {
          alerts.push(
            this.createAlert(
              {
                action: 'resolve',
                audience: 'client',
                message: `To avoid payment delays, your primary bank account needs verification.`,
                route: {
                  name: 'dashboard-client-banking',
                  params: {
                    autoReverifyActiveBank: true,
                    id: this.controlledClient
                      ? this.controlledClient.client.id
                      : this.$auth.user.client.id,
                  },
                },
                submessage: `${this.activeBank.bank_name} | Account number ${this.activeBank.bankInfo.account}`,
                type: 'banking',
              },
              false
            )
          )
        }
        if (this.activeBank.bank_account_owner_name === null) {
          alerts.push(
            this.createAlert(
              {
                action: 'resolve',
                audience: 'client',
                message: `To avoid payment delays, your primary bank account needs the name associated with the account.`,
                route: {
                  name: 'dashboard-client-banking',
                  params: {
                    autoUpdateActiveBank: true,
                    id: this.controlledClient
                      ? this.controlledClient.client.id
                      : this.$auth.user.client.id,
                  },
                },
                type: 'banking',
              },
              false
            )
          )
        }
      }
      return alerts
    },

    createAlert (announcement, closeable = false) {
      return {
        action: announcement.action,
        audience: announcement.audience,
        closeable,
        closed: false,
        message: announcement.message,
        route: announcement.route,
        submessage: announcement.submessage,
        type: announcement?.type || null,
      }
    },

    exitControl () {
      // 1. Reset user from master in localStorage
      const master = localStorage.getItem('master')
      localStorage.setItem('user', master)
      // 2. Remove master and controlledClient in localStorage
      const controlledClient = JSON.parse(localStorage.getItem('controlledClient'))
      localStorage.removeItem('controlledClient')
      localStorage.removeItem('master')
      // 3. Route to the client having been controlled (ending the )
      this.requestSuccess({
        message: `You have now logged out as ${controlledClient.client.shortened_name}. Redirecting...`,
      })
      window.open(`${process.env.VUE_APP_APP_URL}/client/${controlledClient.client.id}/activity`, '_self')
    },

    async getClientBanks (id) {
      try {
        this.clientBanks = (await ClientBankAccount.queryList({
          client_id: id,
        })).data.rows
      }
      catch (error) {
        this.captureSentryEvent(
          'App CREATED "Get Banks"',
          {
            config: error.config,
            data: this.$data,
            details: error,
            id,
            props: this.$props,
            response: error.response,
          }
        )
        this.CError(error)
        this.requestFailure({ message: 'There was an issue getting the client\'s bank accounts' })
      }
    },

    async getNotifications () {
      const announcements = await this.checkForAnnouncements()
      const announcementsClient = announcements.filter((announcement) => {
        return [ 'all', 'client' ].includes(announcement.audience)
      })
      const announcementsEmployee = announcements.filter((announcement) => {
        return [ 'all', 'employee' ].includes(announcement.audience)
      })

      if (this.$auth.user?.client) {
        this.bankingAlerts = await this.checkForBankAlerts()
        this.announcements = announcementsClient
        const v2Announcement =  await LaunchDarkly.isFlagEnabledForClient(
          this.$auth.user.client.id,
          LDFlags.v2Announcement
        )
        if (v2Announcement) {
          this.announcements.push(this.createAlert({
            audience: 'client',
            message: "Upcoming Factoring Web App Update: Keep an eye out for a new look and feel, faster invoice submission, and more improved features!",
          }))
        }
        this.requestSuccess({
          message: `Switching to the new look. Redirecting...`,
        })
        window.open(`${process.env.VUE_APP_V2_UI}`, '_self')
      }
      else {
        this.announcements = announcementsEmployee
        const isStaffV2Enabled = await LaunchDarkly.isFlagEnabled(LDFlags.staffAppV2)
        if (isStaffV2Enabled) {
          this.showNewVersionModal = true
        }
      }
    },

    goToClientBanking () {
      this.$router.push({
        name: 'dashboard-client-banking',
        params: {
          // Only auto trigger adding a new bank if this.clientBanks still equals null or []
          autoTriggerNewBank: !this.clientBanks.length,
          id: this.$auth.user.client.id,
        },
      })
    },

    closeNewVersionModal () {
      this.showNewVersionModal = false
    },

    redirectToV2 () {
      window.open(`${process.env.VUE_APP_STAFF_V2_UI}`, '_self')
    },

    initMediaQueryList () {
      // Get mobile state
      this.mediaQueryList = window.matchMedia('(max-width: 1024px)')
      // Keep mobile state updated: monitor screen res or zoom level changes
      this.addMediaQueryListener()
      this.updateMatch()
    },

    resetData () {
      Object.assign(this.$data, this.$options.data())
      this.initMediaQueryList()
    },

    setSelectVisibility (newRoute = null) {
      const route = newRoute
        ? newRoute.name
        : this.$route.name
      // Navigation doesn't display on these pages
      this.visible = ![
        'admin-login',
        'auth0-callback',
        'invoice-details',
        'login',
        'maintenance',
        'page-not-found',
        'password-reset',
        'permission-denied',
        'privacy',
        'release-notes',
        'set-password',
        'styleguide',
        'terms',
        null,
      ].includes(route)
    },

    async shouldPromptClientAboutBankVerification () {
      if (this.$auth.user?.client?.id) {
        // If the client's ID is one of the listed values for the bank verification flag
        // (This step is last because it involves an asynchronous request)
        return await LaunchDarkly.isFlagEnabledForClient(
          this.$auth.user.client.id,
          LDFlags.bankVerification
        )
      }
    },

    updateMatch () {
      this.$store.commit('STORE_IS_MOBILE', this.mediaQueryList.matches)
    },
  },
}
</script>

<style lang="sass">
@import './styles/base/normalize.sass'
@import './styles/base/icons.sass'
@import './styles/base/typography.sass'
@import './styles/base/spacing.sass'
@import './styles/base/color-classes.sass'
@import './styles/base/transitions.sass'
@import './styles/v-select.sass'

// Depending on the resolution: (account for side menu)
#app
  display: flex
  width: 100%

.column
  +flex($direction: column)
  width: 100%

  &--align-start
    align-items: flex-start

  &--align-center
    align-items: center

  &--align-end
    align-items: flex-end

  &--justify-center
    justify-content: center

  &--justify-between
    justify-content: space-between

  &--justify-end
    justify-content: flex-end

  &--width-auto
    width: auto

.Divider
  background-color: transparent
  border: none
  border-bottom: rem(1px) solid $borders
  width: 100%

.mobile-browsing
  // TODO: Remove if Chatlio gets removed
  padding-bottom: 6rem !important

.row
  +flex()
  width: 100%

  &--wrap
    flex-wrap: wrap

  &--align-center
    align-items: center

  &--align-end
    align-items: flex-end

  &--justify-center
    justify-content: center

  &--justify-between
    justify-content: space-between

  &--justify-end
    justify-content: flex-end

  &--width-auto
    width: auto

// Helper classes
.clickable
  cursor: pointer

.width-100
  width: 100%

.App
  min-height: 100vh

  &__new-version
    +flex()
    gap: 16px

  &__announcement .BaseAlerts
    background-color: $announcement
    border-radius: 0
    position: static
    width: 100vw

  &__client-welcome-text-container
    height: $input-height

  &__environment-label .BaseAlerts--fixed
    border-radius: 0
    bottom: 0
    position: fixed
    width: 100vw

  &__wrapper
    margin: 0 auto
    max-width: rem($xlarge)
    padding: 0

    .BaseSelectSearch
      // This aligns the top of the component with the top of the nav Bobtail logo
      margin-top: rem(48px)

  @media #{$tablet-landscape-and-up}
    // This is the spacing between the router-view content and base employee search
    // This will be overriden in individual components that don't display the BaseEmployeeSelectSearch
    &__router-view
      margin-top: rem(52px)

  @media #{$tablet-landscape-to-xlarge}

    &__wrapper
      padding: 0 1rem
</style>
